// Hand-written claims whose path isn't clean (rcm-26 §8, CLAIM_LIFECYCLE §5).
// They sit in the same list as the generated history, so each opens at
// /claims/record/:id and shows in /claims/all with matching status and
// "where it is" text. Dates are mock, none after Oct 18, 2026.
import type { HistoryClaim, HistoryProcedure, TimelineEntry } from './historyMock';
import { PMS } from '@/features/shared/pms';

const PROVIDER = 'Dr. Emily Carter';
const CONFIRMER = 'Ava Thompson (admin)';

const proc = (code: string, desc: string, tooth: string | null, fee: number): HistoryProcedure => ({ code, desc, tooth, fee });
const codesOf = (ps: HistoryProcedure[]): string[] => [...new Set(ps.map((p) => p.code))];

/** Fills the fields every curated claim shares. */
function make(c: Omit<HistoryClaim, 'codes' | 'provider' | 'billed'>): HistoryClaim {
  return { ...c, provider: PROVIDER, codes: codesOf(c.procedures), billed: c.procedures.reduce((s, p) => s + p.fee, 0) };
}

const SENT = `Sent it from ${PMS} · the clearinghouse accepted it`;

const tl = (stamp: string, lead: string, who: string, detail?: string, extra?: Partial<TimelineEntry>): TimelineEntry => ({
  stamp,
  ok: true,
  lead,
  who,
  ...(detail ? { detail } : {}),
  ...extra,
});

// (a) Rejected, fixed, sent again: Sent x2, red Rejected dot.
const CLM_31380 = make({
  id: 'CLM-31380',
  patient: 'Marisol Reyes',
  payer: 'Delta Dental',
  plan: 'Delta Dental PPO',
  payerClaimNo: 'DE-48120377',
  procedures: [proc('D0120', 'Periodic exam', null, 62), proc('D0274', 'Bitewings, four films', null, 96), proc('D1110', 'Adult cleaning', null, 128)],
  paid: null,
  allowed: null,
  expectedAllowed: 220,
  patientOwes: 0,
  writeOff: null,
  visitIso: '2026-09-14',
  visitShort: 'Sep 14',
  visitFull: 'Sep 14, 2026',
  lastChangeIso: '2026-09-18',
  lastChangeLabel: 'Sep 18',
  status: 'with-payer',
  tone: 'teal',
  where: 'With Delta Dental · after a resend',
  needsPerson: false,
  memberId: 'DD-5521-8306',
  path: [
    { label: 'Prepared', date: 'Sep 14' },
    { label: 'Confirmed', date: 'Sep 15' },
    { label: 'Sent', date: 'Sep 15 · Sep 17', count: 2 },
    { label: 'Rejected', date: 'Sep 16', bad: true },
    { label: 'Accepted', date: 'Sep 18' },
  ],
  nextStep: 'Waiting for Delta Dental',
  nextNote: 'usually by Oct 21',
  timeline: [
    tl('Sep 14, 6:14 PM', 'Prepared the claim', 'ELVA', `from ${PMS}'s draft`),
    tl('Sep 15, 9:02 AM', 'Confirmed the claim', CONFIRMER),
    tl('Sep 15, 9:03 AM', SENT, 'ELVA'),
    tl('Sep 16, 7:40 AM', 'Read the clearinghouse report', 'ELVA', 'Delta Dental rejected the claim before review, the subscriber ID did not match the plan', { ok: false }),
    tl('Sep 16, 10:12 AM', `Corrected the subscriber ID in ${PMS} from the card on file · read back: matches`, 'ELVA'),
    tl('Sep 16, 10:40 AM', 'Confirmed the fix', CONFIRMER),
    tl('Sep 17, 8:31 AM', SENT, 'ELVA'),
    tl('Sep 18, 6:05 AM', 'Delta Dental accepted the claim', 'ELVA'),
  ],
  openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
  band: { pre: 'Marisol’s claim of Sep 14 was rejected once, fixed and sent again. It is ', em: 'with Delta Dental now', post: '.', mint: false },
});

// (b) From before ELVA: starts at the first step ELVA saw, a staff-posted payment.
const CLM_31381 = make({
  id: 'CLM-31381',
  patient: 'Grace Lindqvist',
  payer: 'Guardian',
  plan: 'Guardian DPPO',
  payerClaimNo: 'GU-60214488',
  procedures: [proc('D2391', 'Filling, one surface, back tooth', '#14', 180), proc('D0220', 'Periapical film', null, 40), proc('D1110', 'Adult cleaning', null, 125)],
  paid: 200,
  allowed: 200,
  patientOwes: 0,
  writeOff: 145,
  payments: [{ amount: 200, date: 'Aug 18' }],
  firstSeen: 'Jul 30',
  visitIso: '2026-07-23',
  visitShort: 'Jul 23',
  visitFull: 'Jul 23, 2026',
  lastChangeIso: '2026-08-24',
  lastChangeLabel: 'Aug 24',
  status: 'deposited',
  tone: 'green',
  where: 'Deposited',
  needsPerson: false,
  memberId: 'GU-3340-2175',
  path: [
    { label: 'Sent', date: 'Jul 24', outline: true },
    { label: 'Accepted', date: 'Jul 30' },
    { label: 'Paid', date: 'Aug 18' },
    { label: 'Posted', date: 'Aug 19' },
    { label: 'Deposited', date: 'Aug 24' },
  ],
  timeline: [
    tl('Jul 23', 'Created the claim', PMS, `in ${PMS}`, { fromDentrix: true }),
    tl('Jul 24', 'Sent the claim to Guardian', PMS, `in ${PMS}, outside ELVA`, { fromDentrix: true }),
    tl('Jul 30, 9:02 AM', `Read the claim from ${PMS}`, 'ELVA', 'checked Guardian’s website: received, in review'),
    tl('Aug 18, 10:12 AM', 'Read the payment: Guardian paid $200.00', 'ELVA'),
    tl('Aug 19, 8:31 AM', `Ava Thompson posted $200.00 in ${PMS}`, 'seen by ELVA Aug 19'),
    tl('Aug 24, 2:20 PM', 'Matched the deposit', 'ELVA', 'Guardian EFT'),
  ],
  payerPaidDate: 'Aug 18',
  depositDate: 'Aug 24',
  closedDate: 'Aug 24',
  band: { pre: 'Grace’s claim of Jul 23 was paid in full. ELVA ', em: 'first saw it on Jul 30', post: `, after ${PMS} sent it.`, mint: true },
});

// (c) Paid and posted, but no bank statement yet: hollow Deposited.
const CLM_31382 = make({
  id: 'CLM-31382',
  patient: 'Dylan Okafor',
  payer: 'Cigna',
  plan: 'Cigna DPPO',
  payerClaimNo: 'CI-77310452',
  procedures: [proc('D2740', 'Crown, porcelain', '#19', 290), proc('D0140', 'Limited exam', null, 120)],
  paid: 290,
  allowed: 290,
  patientOwes: 0,
  writeOff: 120,
  payments: [{ amount: 290, date: 'Sep 24' }],
  visitIso: '2026-09-02',
  visitShort: 'Sep 2',
  visitFull: 'Sep 2, 2026',
  lastChangeIso: '2026-09-25',
  lastChangeLabel: 'Sep 25',
  status: 'posted',
  tone: 'green',
  where: 'Posted · waiting for the deposit',
  needsPerson: false,
  memberId: 'U8810-4402',
  path: [
    { label: 'Prepared', date: 'Sep 2' },
    { label: 'Confirmed', date: 'Sep 3' },
    { label: 'Sent', date: 'Sep 3' },
    { label: 'Accepted', date: 'Sep 4' },
    { label: 'Paid', date: 'Sep 24' },
    { label: 'Posted', date: 'Sep 25' },
    { label: 'Deposited', date: '', none: true },
  ],
  timeline: [
    tl('Sep 2, 6:14 PM', 'Prepared the claim', 'ELVA', `from ${PMS}'s draft`),
    tl('Sep 3, 9:02 AM', 'Confirmed the claim', CONFIRMER),
    tl('Sep 3, 9:03 AM', SENT, 'ELVA'),
    tl('Sep 4, 6:05 AM', 'Cigna accepted the claim', 'ELVA'),
    tl('Sep 24, 10:12 AM', 'Read the payment: Cigna paid $290.00', 'ELVA'),
    tl('Sep 25, 7:40 AM', `Posted $290.00 to ${PMS}`, 'ELVA', 'Patient owes $0.00'),
  ],
  payerPaidDate: 'Sep 24',
  noDepositReason: 'No bank statement uploaded after Sep 20',
  openIn: { label: 'Posting', to: '/posting/posted', page: 'posting' },
  band: { pre: 'Dylan’s claim of Sep 2 was paid and posted. ', em: 'No bank statement has been uploaded since Sep 20.', mint: false },
});

// The Liam case (staging fix, Oct 18): billed $331, allowed $200, paid $200.
const CLM_31383 = make({
  id: 'CLM-31383',
  patient: 'Liam Whitaker',
  payer: 'Sun Life',
  plan: 'Sun Life DPPO',
  payerClaimNo: 'SU-30958817',
  procedures: [proc('D0150', 'Comprehensive exam', null, 95), proc('D1110', 'Adult cleaning', null, 140), proc('D0274', 'Bitewings, four films', null, 96)],
  paid: 200,
  allowed: 200,
  patientOwes: 0,
  writeOff: 131,
  payments: [{ amount: 200, date: 'Sep 29' }],
  visitIso: '2026-09-09',
  visitShort: 'Sep 9',
  visitFull: 'Sep 9, 2026',
  lastChangeIso: '2026-10-02',
  lastChangeLabel: 'Oct 2',
  status: 'deposited',
  tone: 'green',
  where: 'Deposited',
  needsPerson: false,
  memberId: 'SL-7720-1913',
  path: [
    { label: 'Prepared', date: 'Sep 9' },
    { label: 'Confirmed', date: 'Sep 9' },
    { label: 'Sent', date: 'Sep 10' },
    { label: 'Accepted', date: 'Sep 11' },
    { label: 'Paid', date: 'Sep 29' },
    { label: 'Posted', date: 'Sep 30' },
    { label: 'Deposited', date: 'Oct 2' },
  ],
  timeline: [
    tl('Sep 9, 8:14 AM', 'Prepared the claim', 'ELVA', `from ${PMS}'s draft`),
    tl('Sep 9, 9:02 AM', 'Confirmed the claim', CONFIRMER),
    tl('Sep 10, 9:03 AM', SENT, 'ELVA'),
    tl('Sep 11, 6:05 AM', 'Sun Life accepted the claim', 'ELVA'),
    tl('Sep 29, 10:12 AM', 'Read the payment: Sun Life paid $200.00', 'ELVA', 'allowed $200.00, contractual write-off $131.00'),
    tl('Sep 30, 7:40 AM', `Posted $200.00 to ${PMS}`, 'ELVA', 'Patient owes $0.00'),
    tl('Oct 2, 2:20 PM', 'Matched the deposit', 'ELVA', 'Sun Life EFT'),
  ],
  payerPaidDate: 'Sep 29',
  depositDate: 'Oct 2',
  closedDate: 'Oct 2',
  band: { pre: 'Liam’s claim of Sep 9 was ', em: 'paid in full', post: '. He owes nothing; $131.00 was written off under the Sun Life contract.', mint: true },
});

// (e1) Secondary claim, linked both ways with its primary (CLM-31385).
const CLM_31384 = make({
  id: 'CLM-31384',
  patient: 'Noor Haddad',
  payer: 'Delta Dental',
  plan: 'Delta Dental PPO',
  payerClaimNo: 'DE-48201166',
  procedures: [proc('D2740', 'Crown, porcelain', '#30', 600)],
  paid: null,
  allowed: null,
  expectedAllowed: 420,
  patientOwes: 0,
  writeOff: null,
  links: [{ label: 'Secondary claim, after Cigna', id: 'CLM-31385' }],
  visitIso: '2026-08-12',
  visitShort: 'Aug 12',
  visitFull: 'Aug 12, 2026',
  lastChangeIso: '2026-09-04',
  lastChangeLabel: 'Sep 4',
  status: 'with-payer',
  tone: 'teal',
  where: 'With Delta Dental · secondary',
  needsPerson: false,
  memberId: 'DD-9017-3358',
  path: [
    { label: 'Prepared', date: 'Sep 2' },
    { label: 'Confirmed', date: 'Sep 2' },
    { label: 'Sent', date: 'Sep 3' },
    { label: 'Accepted', date: 'Sep 4' },
  ],
  nextStep: 'Waiting for Delta Dental',
  nextNote: 'usually by Oct 22',
  timeline: [
    tl('Sep 2, 9:02 AM', 'Prepared the secondary claim', 'ELVA', 'after Cigna’s payment was read, with Cigna’s EOB attached'),
    tl('Sep 2, 9:40 AM', 'Confirmed the claim', CONFIRMER),
    tl('Sep 3, 9:03 AM', SENT, 'ELVA'),
    tl('Sep 4, 6:05 AM', 'Delta Dental accepted the claim', 'ELVA'),
  ],
  openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
  band: { pre: 'Noor’s secondary claim is with Delta Dental. ', em: 'Cigna’s claim is paid.', mint: false },
});

// (e1) The primary.
const CLM_31385 = make({
  id: 'CLM-31385',
  patient: 'Noor Haddad',
  payer: 'Cigna',
  plan: 'Cigna DPPO',
  payerClaimNo: 'CI-77422908',
  procedures: [proc('D2740', 'Crown, porcelain', '#30', 600)],
  paid: 336,
  allowed: 420,
  patientOwes: 84,
  writeOff: 180,
  owesNote: 'The secondary claim to Delta Dental may cover this.',
  payments: [{ amount: 336, date: 'Sep 1' }],
  links: [{ label: 'Secondary claim with Delta Dental', id: 'CLM-31384' }],
  visitIso: '2026-08-12',
  visitShort: 'Aug 12',
  visitFull: 'Aug 12, 2026',
  lastChangeIso: '2026-09-08',
  lastChangeLabel: 'Sep 8',
  status: 'deposited',
  tone: 'green',
  where: 'Deposited · secondary sent',
  needsPerson: false,
  memberId: 'U8810-3119',
  path: [
    { label: 'Prepared', date: 'Aug 12' },
    { label: 'Confirmed', date: 'Aug 13' },
    { label: 'Sent', date: 'Aug 13' },
    { label: 'Accepted', date: 'Aug 14' },
    { label: 'Paid', date: 'Sep 1' },
    { label: 'Posted', date: 'Sep 2' },
    { label: 'Deposited', date: 'Sep 8' },
  ],
  timeline: [
    tl('Aug 12, 6:14 PM', 'Prepared the claim', 'ELVA', `from ${PMS}'s draft`),
    tl('Aug 13, 9:02 AM', 'Confirmed the claim', CONFIRMER),
    tl('Aug 13, 9:03 AM', SENT, 'ELVA'),
    tl('Aug 14, 6:05 AM', 'Cigna accepted the claim', 'ELVA'),
    tl('Sep 1, 10:12 AM', 'Read the payment: Cigna paid $336.00 of $420.00 allowed', 'ELVA'),
    tl('Sep 2, 7:40 AM', `Posted $336.00 to ${PMS}`, 'ELVA', 'Patient owes $84.00'),
    tl('Sep 2, 9:02 AM', 'Made the secondary claim for Delta Dental', 'ELVA', 'CLM-31384'),
    tl('Sep 8, 2:20 PM', 'Matched the deposit', 'ELVA', 'Cigna EFT'),
  ],
  payerPaidDate: 'Sep 1',
  depositDate: 'Sep 8',
  closedDate: 'Sep 8',
  band: { pre: 'Noor’s claim of Aug 12 was paid by Cigna. The rest went to Delta Dental ', em: 'as a secondary claim', post: '.', mint: true },
});

// (e2) Corrected claim: the old one is replaced and links to the new one.
const CLM_31386 = make({
  id: 'CLM-31386',
  patient: 'Rosa Delgado',
  payer: 'Aetna',
  plan: 'Aetna PPO',
  payerClaimNo: 'AE-55103420',
  procedures: [proc('D2740', 'Crown, porcelain', '#19', 540)],
  paid: null,
  allowed: null,
  expectedAllowed: 380,
  patientOwes: 0,
  writeOff: null,
  links: [{ label: 'Replaced by', id: 'CLM-31387' }],
  visitIso: '2026-08-25',
  visitShort: 'Aug 25',
  visitFull: 'Aug 25, 2026',
  lastChangeIso: '2026-09-03',
  lastChangeLabel: 'Sep 3',
  status: 'replaced',
  tone: 'grey',
  where: 'Replaced by CLM-31387',
  needsPerson: false,
  memberId: 'W281447710',
  path: [
    { label: 'Prepared', date: 'Aug 25' },
    { label: 'Confirmed', date: 'Aug 26' },
    { label: 'Sent', date: 'Aug 26' },
    { label: 'Accepted', date: 'Aug 27' },
    { label: 'Replaced', date: 'Sep 3' },
  ],
  timeline: [
    tl('Aug 25, 6:14 PM', 'Prepared the claim', 'ELVA', `from ${PMS}'s draft`),
    tl('Aug 26, 9:02 AM', 'Confirmed the claim', CONFIRMER),
    tl('Aug 26, 9:03 AM', SENT, 'ELVA'),
    tl('Aug 27, 6:05 AM', 'Aetna accepted the claim', 'ELVA'),
    tl('Sep 3, 8:31 AM', 'Voided the claim', 'ELVA', 'the crown was on tooth #18, not #19. Replaced by CLM-31387'),
  ],
  closedDate: 'Sep 3',
  band: { pre: 'Rosa’s claim of Aug 25 had the wrong tooth. It was ', em: 'replaced by a corrected claim', post: '.', mint: false },
});

const CLM_31387 = make({
  id: 'CLM-31387',
  patient: 'Rosa Delgado',
  payer: 'Aetna',
  plan: 'Aetna PPO',
  payerClaimNo: 'AE-55188731',
  procedures: [proc('D2740', 'Crown, porcelain', '#18', 540)],
  paid: null,
  allowed: null,
  expectedAllowed: 380,
  patientOwes: 0,
  writeOff: null,
  links: [{ label: 'Replaces', id: 'CLM-31386' }],
  visitIso: '2026-08-25',
  visitShort: 'Aug 25',
  visitFull: 'Aug 25, 2026',
  lastChangeIso: '2026-09-05',
  lastChangeLabel: 'Sep 5',
  status: 'with-payer',
  tone: 'teal',
  where: 'With Aetna · corrected claim',
  needsPerson: false,
  memberId: 'W281447710',
  path: [
    { label: 'Prepared', date: 'Sep 3' },
    { label: 'Confirmed', date: 'Sep 3' },
    { label: 'Sent', date: 'Sep 4' },
    { label: 'Accepted', date: 'Sep 5' },
  ],
  nextStep: 'Waiting for Aetna',
  nextNote: 'usually by Oct 22',
  timeline: [
    tl('Sep 3, 8:35 AM', 'Prepared the corrected claim', 'ELVA', 'tooth #18, replacing CLM-31386'),
    tl('Sep 3, 9:02 AM', 'Confirmed the claim', CONFIRMER),
    tl('Sep 4, 9:03 AM', SENT, 'ELVA'),
    tl('Sep 5, 6:05 AM', 'Aetna accepted the claim', 'ELVA'),
  ],
  openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
  band: { pre: 'Rosa’s corrected claim is with Aetna. It ', em: 'replaces CLM-31386', post: '.', mint: false },
});

// (d) Recoupment: a payment taken back, the claim reopens.
const CLM_31388 = make({
  id: 'CLM-31388',
  patient: 'Omar Rahimi',
  payer: 'Sun Life',
  plan: 'Sun Life DPPO',
  payerClaimNo: 'SU-30771204',
  procedures: [proc('D2750', 'Crown, porcelain to metal', '#3', 480)],
  paid: 240,
  allowed: 320,
  patientOwes: 0,
  writeOff: 160,
  owesNote: 'The $80.00 taken back is not billed to the patient while the claim is open.',
  payments: [
    { amount: 320, date: 'Aug 14' },
    { amount: -80, date: 'Sep 8' },
  ],
  visitIso: '2026-07-29',
  visitShort: 'Jul 29',
  visitFull: 'Jul 29, 2026',
  lastChangeIso: '2026-10-15',
  lastChangeLabel: 'Oct 15',
  status: 'taken-back',
  tone: 'red',
  where: 'Taken back · ELVA following up',
  needsPerson: false,
  memberId: 'SL-4415-6620',
  path: [
    { label: 'Prepared', date: 'Jul 29' },
    { label: 'Confirmed', date: 'Jul 30' },
    { label: 'Sent', date: 'Jul 30' },
    { label: 'Accepted', date: 'Jul 31' },
    { label: 'Paid', date: 'Aug 14' },
    { label: 'Posted', date: 'Aug 15' },
    { label: 'Deposited', date: 'Aug 19' },
    { label: 'Taken back', date: 'Sep 8', bad: true },
  ],
  nextStep: 'Waiting for Sun Life',
  nextNote: 'next check Oct 21',
  timeline: [
    tl('Jul 29, 8:14 AM', 'Prepared the claim', 'ELVA', `from ${PMS}'s draft`),
    tl('Jul 30, 9:02 AM', 'Confirmed the claim', CONFIRMER),
    tl('Jul 30, 9:03 AM', SENT, 'ELVA'),
    tl('Jul 31, 6:05 AM', 'Sun Life accepted the claim', 'ELVA'),
    tl('Aug 14, 10:12 AM', 'Read the payment: Sun Life paid $320.00', 'ELVA'),
    tl('Aug 15, 7:40 AM', `Posted $320.00 to ${PMS}`, 'ELVA', 'Patient owes $0.00'),
    tl('Aug 19, 2:20 PM', 'Matched the deposit', 'ELVA', 'Sun Life EFT'),
    tl('Sep 8, 7:40 AM', 'Sun Life took back $80.00', 'ELVA', 'the ERA says the payment went to the wrong claim (offset on check 4471)', { ok: false, bad: true }),
    tl('Sep 8, 9:03 AM', 'Opened the claim again', 'ELVA', 'in Insurance A/R · ELVA is asking Sun Life why'),
    tl('Oct 12, 8:46 AM', 'Asked Sun Life why', 'ELVA', 'called, reference TB-20418'),
    tl('Oct 15, 1:12 PM', 'Checked Sun Life’s website: the offset is under review', 'ELVA'),
  ],
  payerPaidDate: 'Aug 14',
  depositDate: 'Aug 19',
  openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
  band: { pre: 'Sun Life took back $80.00 on Omar’s claim of Jul 29. ', em: 'It’s open again; ELVA is asking Sun Life why.', mint: false },
});

// (f) Sent outside ELVA (D209): the practice's staff sent it from the PMS; ELVA found it on the
// Oct 16 sync. Its record starts there, then it follows the normal life. With the payer, so it
// is not waiting for anyone and does not touch the "need a person" count.
const CLM_31389 = make({
  id: 'CLM-31389',
  patient: 'Elena Voss',
  payer: 'Aetna',
  plan: 'Aetna PPO',
  payerClaimNo: 'AE-55240917',
  procedures: [proc('D1110', 'Adult cleaning', null, 110), proc('D0120', 'Periodic exam', null, 62), proc('D0274', 'Bitewings, four films', null, 82)],
  paid: null,
  allowed: null,
  expectedAllowed: 224,
  patientOwes: 0,
  writeOff: null,
  firstSeen: 'Oct 16',
  visitIso: '2026-10-12',
  visitShort: 'Oct 12',
  visitFull: 'Oct 12, 2026',
  lastChangeIso: '2026-10-17',
  lastChangeLabel: 'Oct 17',
  status: 'with-payer',
  tone: 'teal',
  where: `With Aetna · Sent from ${PMS} by your team`,
  needsPerson: false,
  memberId: 'W407215583',
  path: [
    { label: 'Sent', date: 'Oct 14', outline: true },
    { label: 'Accepted', date: 'Oct 17' },
  ],
  nextStep: 'Waiting for Aetna',
  nextNote: 'usually by Nov 16',
  timeline: [
    tl('Oct 16, 6:05 AM', `Found on the Oct 16 sync: sent from ${PMS} without ELVA`, 'ELVA', 'your team sent it on Oct 14'),
    tl('Oct 17, 6:05 AM', 'Aetna accepted the claim', 'ELVA'),
  ],
  openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
  band: { pre: 'Elena’s claim of Oct 12 was ', em: `sent from ${PMS} by your team`, post: '. It is with Aetna.', mint: false },
});

const CLM_31390 = make({
  id: 'CLM-31390',
  patient: 'Darius Whitfield',
  payer: 'Cigna',
  plan: 'Cigna DPPO',
  payerClaimNo: 'CI-77518204',
  procedures: [proc('D2392', 'Resin, two surfaces', '#30', 230), proc('D0220', 'Periapical X-ray', '#30', 35)],
  paid: null,
  allowed: null,
  expectedAllowed: 234,
  patientOwes: 0,
  writeOff: null,
  firstSeen: 'Oct 16',
  visitIso: '2026-10-06',
  visitShort: 'Oct 6',
  visitFull: 'Oct 6, 2026',
  lastChangeIso: '2026-10-16',
  lastChangeLabel: 'Oct 16',
  status: 'with-payer',
  tone: 'teal',
  where: `With Cigna · Sent from ${PMS} by your team`,
  needsPerson: false,
  memberId: 'U9921-5036',
  path: [
    { label: 'Sent', date: 'Oct 9', outline: true },
    { label: 'Accepted', date: 'Oct 16' },
  ],
  nextStep: 'Waiting for Cigna',
  nextNote: 'usually by Nov 15',
  timeline: [
    tl('Oct 16, 6:05 AM', `Found on the Oct 16 sync: sent from ${PMS} without ELVA`, 'ELVA', 'your team sent it on Oct 9'),
    tl('Oct 16, 6:20 AM', 'Checked Cigna’s website: received, in review', 'ELVA'),
  ],
  openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
  band: { pre: 'Darius’s claim of Oct 6 was ', em: `sent from ${PMS} by your team`, post: '. It is with Cigna.', mint: false },
});

// (g) Checking if it was sent (D247): the submission ended without confirmation. ELVA is checking the PMS
// and the clearinghouse before it sends again, and never sends twice. No actions for the practice, and
// it is not "need a person": ELVA is working on it. No payer claim number and no Sent date yet.
const CHECKING_LINE = `ELVA’s last send to ${PMS} didn’t confirm. ELVA is checking ${PMS} and the clearinghouse before sending again. It never sends twice.`;
const CHECKING_ASIDE = 'Nothing for you to do here. ELVA is checking, and it never sends the claim twice. This claim is not counted in Claims to review.';

const CLM_31391 = make({
  id: 'CLM-31391',
  patient: 'Tessa Brandt',
  payer: 'Aetna',
  plan: 'Aetna PPO',
  payerClaimNo: '',
  procedures: [proc('D2740', 'Crown, porcelain', '#30', 540), proc('D2950', 'Core build-up', '#30', 150)],
  paid: null,
  allowed: null,
  expectedAllowed: 452,
  patientOwes: 0,
  writeOff: null,
  visitIso: '2026-10-12',
  visitShort: 'Oct 12',
  visitFull: 'Oct 12, 2026',
  lastChangeIso: '2026-10-17',
  lastChangeLabel: 'Oct 17',
  status: 'checking-if-sent',
  tone: 'amber',
  where: 'Checking if it was sent',
  needsPerson: false,
  memberId: 'W281-70-3365',
  path: [
    { label: 'Prepared', date: 'Oct 12' },
    { label: 'Confirmed', date: 'Oct 16' },
    { label: 'Sent', date: '', none: true },
  ],
  nextStep: 'Checking if it was sent',
  nextNote: `ELVA is checking ${PMS} and the clearinghouse`,
  timeline: [
    tl('Oct 12, 6:14 PM', 'Prepared the claim', 'ELVA', `from ${PMS}'s draft`),
    tl('Oct 16, 9:02 AM', 'Confirmed the claim', CONFIRMER),
    tl('Oct 17, 9:03 AM', `Tried to send the claim from ${PMS}`, 'ELVA', 'opened claim 4417 · pressed send · no confirmation came back', { ok: false }),
    tl('Oct 17, 9:20 AM', `Checked ${PMS}: the claim shows no sent date`, 'ELVA'),
    tl('Oct 17, 9:41 AM', 'Checked the clearinghouse: no report for this claim yet', 'ELVA'),
    tl('Oct 17, 9:42 AM', 'Kept the claim from going out twice', 'ELVA', 'it will check again before doing anything else'),
  ],
  statusLine: CHECKING_LINE,
  asideNote: CHECKING_ASIDE,
  band: { pre: 'Tessa’s claim of Oct 12 was confirmed, but the send didn’t confirm. ', em: 'ELVA is checking before it sends again.', mint: false },
});

const CLM_31392 = make({
  id: 'CLM-31392',
  patient: 'Miguel Santos',
  payer: 'Delta Dental',
  plan: 'Delta Dental PPO',
  payerClaimNo: '',
  procedures: [proc('D0150', 'Comprehensive exam', null, 95), proc('D0210', 'Full-mouth X-rays', null, 125), proc('D1110', 'Adult cleaning', null, 140)],
  paid: null,
  allowed: null,
  expectedAllowed: 312,
  patientOwes: 0,
  writeOff: null,
  visitIso: '2026-10-09',
  visitShort: 'Oct 9',
  visitFull: 'Oct 9, 2026',
  lastChangeIso: '2026-10-17',
  lastChangeLabel: 'Oct 17',
  status: 'checking-if-sent',
  tone: 'amber',
  where: 'Checking if it was sent',
  needsPerson: false,
  memberId: 'DD-6603-1182',
  path: [
    { label: 'Prepared', date: 'Oct 9' },
    { label: 'Confirmed', date: 'Oct 15' },
    { label: 'Sent', date: '', none: true },
  ],
  nextStep: 'Checking if it was sent',
  nextNote: `ELVA is checking ${PMS} and the clearinghouse`,
  timeline: [
    tl('Oct 9, 5:48 PM', 'Prepared the claim', 'ELVA', `from ${PMS}'s draft`),
    tl('Oct 15, 8:51 AM', 'Confirmed the claim', CONFIRMER),
    tl('Oct 16, 9:03 AM', `Tried to send the claim from ${PMS}`, 'ELVA', 'opened claim 4402 · pressed send · the window closed before it confirmed', { ok: false }),
    tl('Oct 16, 9:25 AM', `Checked ${PMS}: the claim shows no sent date`, 'ELVA'),
    tl('Oct 16, 9:47 AM', 'Checked the clearinghouse: no report for this claim yet', 'ELVA'),
    tl('Oct 17, 6:10 AM', 'Checked the clearinghouse again: still no report', 'ELVA', 'it will keep checking before it sends again'),
  ],
  statusLine: CHECKING_LINE,
  asideNote: CHECKING_ASIDE,
  band: { pre: 'Miguel’s claim of Oct 9 was confirmed, but the send didn’t confirm. ', em: 'ELVA is checking before it sends again.', mint: false },
});

export const CURATED_CLAIMS: HistoryClaim[] = [
  CLM_31389,
  CLM_31390,
  CLM_31391,
  CLM_31392,
  CLM_31380,
  CLM_31381,
  CLM_31382,
  CLM_31383,
  CLM_31384,
  CLM_31385,
  CLM_31386,
  CLM_31387,
  CLM_31388,
];
